import { useDispatch, useSelector } from 'react-redux';
import React, { useState } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';

import { DropdownOption } from 'components/Common/ValidatedDropdown';
import { makeStyles } from '@mui/styles';
import { JBColors, JBTheme } from '@juliusbaer-gcrm/gcrm-frontend-components';
import { User } from 'types/User';
import { GlobalState } from 'reducers/stateTypes';
import { selectIsUsedInBr } from 'selectors/contactNoteListSelectors';
import ContactNote from 'types/ContactNote';
import { Form, Formik, FormikErrors } from 'formik';
import contactNoteValidationSchema from 'views/ContactNoteForm/components/contactNoteValidationSchema';
import contactNoteService from 'services/contactNoteService';
import { ContactNoteStatus } from 'views/ContactNotes/components/ContactNoteRows';
import { initContactNotes, updateContactNote } from 'reducers/contactNoteReducer';
import { getRmFullName } from 'utils/contactNoteUtil';
import { setCnError, setNoteDisplayDialog, setShownNote } from 'reducers/contactNoteListReducer';
import { DisplayDialogs } from 'views/ContactNotes/ContactNotesContainer';
import { openModal } from 'reducers/modalReducer';
import { ScrollToError } from 'views/ContactNoteForm/components/ScrollToError';
import { Box } from '@mui/material';
import ContactNoteForm from 'views/ContactNoteForm/components/ContactNoteForm';
import WarningDialog from 'components/Common/WarningDialog';
import EditFooter from 'views/ContactNoteForm/components/EditFooter';
import SubmissionFooter from 'views/ContactNoteForm/components/SubmissionFooter';

const formStyles = makeStyles({
  form: {
    minWidth: 120,
    maxWidth: 896,
    height: '100%',
    backgroundColor: JBColors.support.greenSmoke[10],
    paddingTop: JBTheme.spacing(0.5),
    marginTop: 0,
  },
});

export interface PartnerSearchResult extends DropdownOption {
  ownPartner: boolean;
  globalPartnerId?: string; // Global Partner ID
  relationshipManager?: string; // RM name
  partnerType?: string; // Individual/Legal Entity
  dateOfBirth?: string; // Date of birth
  domicile?: string; // Domicile country
}

/**
 * Container component for displaying the contact note creation form.
 */
const ContactNoteFormContainer = (props: { cancelAction: any; edit: boolean; contactNote: any }) => {
  const dispatch = useDispatch();
  const user: User = useSelector((state: GlobalState) => state.user.userDetails);
  const dateRange: any[] = useSelector((state: GlobalState) => state.contactNoteList.dateRange);
  const subjectSearch: string = useSelector((state: GlobalState) => state.contactNoteList.subjectSearch);
  const [shouldNoteSubmit, setNoteSubmit] = useState(false);
  const [isConfirmationPassed, setConfirmationPassed] = useState(false);
  const { techId } = useParams<{ techId: string }>();
  const classes = formStyles();
  const { t } = useTranslation();
  const usedInBr = useSelector(selectIsUsedInBr);

  const contactNoteToEdit: ContactNote = props.contactNote
    ? {
        ...props.contactNote,
        partner: props.contactNote?.partner?.partnerUId
          ? props.contactNote?.partner
          : usedInBr
            ? null
            : {
                partnerUId: techId,
                fullName: '',
                relationshipManager: {
                  uid: user.username,
                  fullName: `${user.givenName} ${user.sureName}`,
                },
              },
        //Cast boolean values to string so that it complies with Formik
        crossBorderActivity:
          props.contactNote.crossBorderActivity == null ? '' : props.contactNote.crossBorderActivity.toString(),
        qualifiedClientContact:
          props.contactNote.qualifiedClientContact == null ? '' : props.contactNote.qualifiedClientContact.toString(),
      }
    : {
        partner: usedInBr
          ? null
          : {
              partnerUId: techId,
              fullName: '',
              relationshipManager: {
                uid: user.username,
                fullName: `${user.givenName} ${user.sureName}`,
              },
            },
        relatedPartners: [],
        additionalAttendees: [],
        subject: '',
        contactType: '',
        startDateTime: null,
        endDateTime: null,
        locationCountry: '',
        locationCity: '',
        topics: [],
        initiatedBy: '',
        notes: '',
        createdBy: user.username,
        attachments: [],
        qualifiedClientContact: '',
        crossBorderActivity: '',
        direction: undefined,
        brUId: usedInBr ? techId : null,
      };

  return (
    <Formik
      validateOnBlur
      validateOnChange
      validateOnMount={true}
      initialValues={contactNoteToEdit}
      validationSchema={contactNoteValidationSchema(usedInBr || !!props.contactNote?.brUId)}
      onSubmit={async (data, { setSubmitting, resetForm }) => {
        setSubmitting(true);
        try {
          if (props.edit && isConfirmationPassed) {
            const updatedData: ContactNote = await contactNoteService.updateContactNote(
              contactNoteToEdit.id,
              shouldNoteSubmit
                ? {
                    ...data,
                    status: ContactNoteStatus.SUBMITTED,
                  }
                : data,
            );
            dispatch(
              updateContactNote({
                ...updatedData,
                partnerFullName: data.partner.fullName,
                responsibleRmFullName: getRmFullName(data),
              }),
            );
            dispatch(
              setShownNote({
                ...updatedData,
                partnerFullName: data.partner.fullName,
                responsibleRmFullName: getRmFullName(data),
              }),
            );
            setNoteSubmit(false);
            setConfirmationPassed(false);
            resetForm();
            props.cancelAction();
            dispatch(setNoteDisplayDialog(DisplayDialogs.DEFAULT_NOTE_VIEW));
          } else {
            if (isConfirmationPassed || !shouldNoteSubmit) {
              await contactNoteService.create({
                ...data,
                status: shouldNoteSubmit ? ContactNoteStatus.SUBMITTED : ContactNoteStatus.EDITABLE,
              });
              const notes = await contactNoteService.getMyNotes(
                '',
                false,
                subjectSearch,
                dateRange,
                '',
                techId,
                usedInBr,
              );
              dispatch(setCnError(''));
              dispatch(initContactNotes(notes));
              setNoteSubmit(false);
              setConfirmationPassed(false);
              resetForm();
              props.cancelAction();
              dispatch(setNoteDisplayDialog(DisplayDialogs.DEFAULT_NOTE_VIEW));
            }
          }
        } catch (exception) {
          dispatch(
            openModal({
              modalType: 'ErrorDialog',
              modalProps: { error: exception },
            }),
          );
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({
        isSubmitting,
        submitForm,
        isValid,
        resetForm,
      }: {
        values: ContactNote;
        errors: FormikErrors<ContactNote>;
        isSubmitting: boolean;
        submitForm: () => Promise<any>;
        isValid: boolean;
        resetForm: any;
      }) => (
        <Form className={classes.form}>
          <ScrollToError />
          <Box ml={2}>
            <ContactNoteForm
              title={
                props.edit
                  ? t('noteform.title.edit', 'Edit contact note')
                  : t('noteform.title.new', 'Create new contact note')
              }
              showExpandButton={false}
            />
          </Box>
          {/*<pre>{JSON.stringify(values, null, 2)}</pre>*/}
          {/*<pre>{JSON.stringify(errors, null, 2)}</pre>*/}

          <WarningDialog
            dialogOpen={isValid && shouldNoteSubmit}
            bodyText={
              'Do you really want to submit the contact note? \n' +
              "After submission, the contact note won't be editable anymore."
            }
            affirmText="Yes, submit"
            title="Submission"
            isLoading={isSubmitting}
            disabled={isSubmitting}
            action={() => {
              setConfirmationPassed(true);
              submitForm();
            }}
            cancelAction={() => setNoteSubmit(false)}
            isCancelNeeded={true}
          />

          {props.edit ? (
            <EditFooter
              onClick={props.cancelAction}
              onClickCapture={() => {
                resetForm();
              }}
              disabled={isSubmitting}
              onSubmitClick={() => {
                isValid && setNoteSubmit(true);
              }}
              onSaveClick={() => {
                isValid && setConfirmationPassed(true);
                submitForm();
              }}
            />
          ) : (
            <SubmissionFooter
              onClick={props.cancelAction}
              onClickCapture={() => {
                resetForm();
              }}
              disabled={isSubmitting}
              onSubmitClick={() => {
                isValid && setNoteSubmit(true);
              }}
            />
          )}
        </Form>
      )}
    </Formik>
  );
};

export default ContactNoteFormContainer;
